import BigNumber from 'bignumber.js';
import { EventLog, Log, TransactionReceipt } from 'web3-core';

import { BaseContract, PayableTx } from './typechain/types';
import {
  AbiItemWithSignature,
  EventPayload,
  Events,
  GetGasInfoParams,
  GetGasInfoResult,
  Web3Provider,
} from './types';
import { convertToHex, convertToWei } from './utils';

const ESTIMATE_GAS_MULTIPLIER = new BigNumber(2);

function isEventLog(log: EventLog | Log): log is EventLog {
  return 'raw' in log;
}

export abstract class UtilsManager {
  protected provider: Web3Provider;

  constructor(provider: Web3Provider) {
    this.provider = provider;
  }

  abstract get currentAccount(): string | null;

  // TODO: to move the code to the common provider
  public async getGasInfo<
    Contract extends BaseContract,
    Method extends keyof Contract['methods'],
  >({
    args,
    value,
    contract,
    method,
    currentAccount,
    multiplier = ESTIMATE_GAS_MULTIPLIER,
  }: GetGasInfoParams<Contract, Method>): Promise<GetGasInfoResult> {
    const estimateGasParams: PayableTx = {
      from: currentAccount,
    };

    if (value) {
      estimateGasParams.value = convertToHex(value);
    }

    const estimatedGas = await contract.methods[method](
      ...(args ?? []),
    ).estimateGas(estimateGasParams);

    // it's gas fee * gas price
    const gasRatio = await this.provider.getContractMethodFee(
      Math.round(
        new BigNumber(estimatedGas).multipliedBy(multiplier).toNumber(),
      ),
    );
    const gasPrice = await this.provider.getSafeGasPriceWei();

    return {
      gasRatio,
      gasPrice,
      gasLimit: new BigNumber(convertToWei(gasRatio)).div(gasPrice),
    };
  }

  public async hasMethod(
    contractAddress: string,
    signature: string,
  ): Promise<boolean> {
    const web3 = this.provider.getWeb3();
    const code = await web3.eth.getCode(contractAddress);
    const encodedSignature = web3.eth.abi.encodeFunctionSignature(signature);
    // remove "0x" prefixed in 0x<4bytes-selector>
    return code.indexOf(encodedSignature.slice(2, encodedSignature.length)) > 0;
  }

  public async getEventsFromReceipt<
    EventName extends keyof Events<Contract>,
    Contract extends BaseContract,
  >(
    contract: Contract,
    eventName: EventName,
    transactionHash: string,
  ): Promise<EventPayload<Contract, EventName>[]> {
    const web3 = this.provider.getWeb3();
    const receipt = JSON.parse(
      JSON.stringify(await web3.eth.getTransactionReceipt(transactionHash)),
    ) as TransactionReceipt;

    const abiItem: AbiItemWithSignature | undefined =
      contract.options.jsonInterface.find(
        ({ name, type }) => name === eventName && type === 'event',
      );

    const signature = abiItem?.signature;
    if (!signature) {
      return [];
    }

    const logs = (() => {
      if (Array.isArray(receipt.logs)) {
        return receipt.logs.filter(log => log.topics.includes(signature));
      }

      const log = receipt?.events?.[eventName as string];
      if (log) {
        return [log];
      }

      return [];
    })();
    if (!logs.length) {
      return [];
    }

    return Promise.all(
      logs.map(log => {
        const data = isEventLog(log) ? log?.raw?.data ?? '' : log.data;
        const topics = isEventLog(log) ? log?.raw?.topics || [] : log.topics;
        const eventInputs = abiItem.inputs
          ? web3.eth.abi.decodeLog(abiItem.inputs, data, topics.slice(1))
          : {};

        return {
          address: log.address,
          blockHash: receipt.blockHash,
          blockNumber: receipt.blockNumber,
          event: eventName as string,
          logIndex: log.logIndex,
          raw: {
            data,
            topics,
          },
          returnValues: eventInputs,
          signature: abiItem.signature,
          transactionHash: receipt.transactionHash,
          transactionIndex: receipt.transactionIndex,
        };
      }),
    );
  }
}
